/**
 * AuditLogsPage — 审计日志列表（docs/ui_page 规范）
 * 结构：查询面板（关键词 + 数量限制 + 操作按钮）+ 数据表（PagedGrid）
 * 行点击进入详情页（single 模式）。
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Card,
  Field,
  InfoLabel,
  Combobox,
  Option,
  Switch,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
} from '@fluentui/react-components'
import { SearchRegular, DismissRegular } from '@fluentui/react-icons'
import { auditLogsApi } from '@/api/audit-logs'
import type { AuditLog } from '@/api/types'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid, DetailField } from '@/components/DetailField'
import { formatDate } from '@/lib/format'

const LIMITS = [50, 100, 200, 500]
const PAGE_SIZE = 10

// ── 样式 ──
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '8px 12px',
  },
  queryCard: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  formRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'flex-end',
  },
  keywordField: {
    flex: '2 0 240px',
    minWidth: '220px',
    flexShrink: 0,
  },
  limitField: {
    flex: '1 0 180px',
    minWidth: '160px',
    flexShrink: 0,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '12px',
  },
  tableCard: {
    padding: '16px',
  },
  dialogSurface: {
    width: '65vw',
    minWidth: '600px',
    maxWidth: '1000px',
  },
})

// ── 组件 ──
export default function AuditLogsPage() {
  const styles = useStyles()

  const [limit, setLimit] = useState(100)
  const [detailLog, setDetailLog] = useState<AuditLog | null>(null)

  // 多选（docs/ui_page §4.2：工具栏「多选」开关；只读页无批量删除，仅复选框 + 计数）
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')

  const { data: logs, isLoading } = useQuery({
    queryKey: ['audit-logs', limit],
    queryFn: () => auditLogsApi.list(limit),
  })

  // 查询草稿态
  const [draftQ, setDraftQ] = useState('')
  const [query, setQuery] = useState('')

  // 分页
  const [page, setPage] = useState(1)

  // 前端过滤
  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    const list = logs ?? []
    if (!q) return list
    return list.filter(
      (l) =>
        l.action.toLowerCase().includes(q) ||
        (l.actor ?? '').toLowerCase().includes(q) ||
        (l.target ?? '').toLowerCase().includes(q) ||
        (l.detail ?? '').toLowerCase().includes(q),
    )
  }, [logs, q])

  const hasActiveFilter = !!q
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const resetQuery = () => {
    setDraftQ('')
    setQuery('')
    setPage(1)
  }

  const applyQuery = () => {
    setQuery(draftQ)
    setPage(1)
  }

  const columns: PagedColumn<AuditLog>[] = [
    {
      key: 'action',
      header: '操作',
      width: '200px',
      render: (l) => <Text weight="semibold">{l.action}</Text>,
    },
    {
      key: 'actor',
      header: '操作者',
      width: '160px',
      render: (l) => l.actor ?? '-',
    },
    {
      key: 'target',
      header: '目标',
      width: '200px',
      render: (l) => l.target ?? '-',
    },
    {
      key: 'detail',
      header: '详情',
      width: '240px',
      render: (l) => l.detail ?? '-',
    },
    {
      key: 'ip_addr',
      header: 'IP',
      width: '140px',
      render: (l) => l.ip_addr ?? '-',
    },
    {
      key: 'created_at',
      header: '时间',
      width: '160px',
      render: (l) => formatDate(l.created_at),
    },
  ]

  return (
    <div className={styles.root}>
      {/* ① 查询面板 */}
      <Card className={styles.queryCard}>
        <div className={styles.formRow}>
          <Field
            className={styles.keywordField}
            label={<InfoLabel info="按操作、操作者、目标或详情模糊搜索，留空显示全部。">关键词</InfoLabel>}
          >
            <Input
              contentBefore={<SearchRegular />}
              contentAfter={
                draftQ ? (
                  <Button
                    appearance="transparent"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="清空"
                    onClick={() => setDraftQ('')}
                  />
                ) : undefined
              }
              placeholder="搜索操作 / 操作者 / 目标…"
              value={draftQ}
              onChange={(_, d) => setDraftQ(d.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyQuery()
              }}
            />
          </Field>
          <Field
            className={styles.limitField}
            label={<InfoLabel info="从后端拉取的日志条数上限。">加载条数</InfoLabel>}
          >
            <Combobox
              defaultSelectedOptions={[String(limit)]}
              onOptionSelect={(_, d) => setLimit(Number(d.optionValue) || 100)}
            >
              {LIMITS.map((l) => (
                <Option key={l} value={String(l)}>{`最近 ${l} 条`}</Option>
              ))}
            </Combobox>
          </Field>
        </div>
        {hasActiveFilter && (
          <div className={styles.filterRow}>
            已应用过滤：关键词 “{query}”
          </div>
        )}
        <div className={styles.actionsRow}>
          <Button appearance="subtle" onClick={resetQuery}>
            重置
          </Button>
          <Button appearance="primary" onClick={applyQuery}>
            查询
          </Button>
        </div>
      </Card>

      {/* ② 数据表 */}
      <Card className={styles.tableCard}>
        <PagedGrid<AuditLog>
          rows={pageItems}
          columns={columns}
          loading={isLoading}
          rowKey={(l) => String(l.id)}
          total={filtered.length}
          page={safePage}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          emptyText="暂无审计日志"
          toolbarLeft={
            <Switch
              checked={selectionMode === 'multiselect'}
              onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
              label="多选"
            />
          }
          selectionMode={selectionMode}
          onRowClick={(l) => setDetailLog(l)}
        />
      </Card>

      {/* ③ 详情 Dialog（modalType="alert"，行点击弹出只读详情） */}
      <Dialog
        open={detailLog !== null}
        onOpenChange={(_, d) => !d.open && setDetailLog(null)}
        modalType="alert"
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="审计日志详情" onClose={() => setDetailLog(null)} />
            <DialogContent>
              {detailLog && (
                <DetailGrid key={detailLog.id}>
                  <DetailField label="ID" value={detailLog.id} mono />
                  <DetailField label="操作" value={detailLog.action} mono help="本次审计记录的操作类型。" />
                  <DetailField label="操作者" value={detailLog.actor ?? '-'} mono />
                  <DetailField label="目标" value={detailLog.target ?? '-'} mono help="被操作的对象，如用户、域名或资源标识。" />
                  <DetailField label="详情" value={detailLog.detail ?? '-'} colSpan />
                  <DetailField label="IP 地址" value={detailLog.ip_addr ?? '-'} mono help="触发该操作时的来源 IP。" />
                  <DetailField label="时间" value={detailLog.created_at} />
                </DetailGrid>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}